/**
 * Speaker Source — captures microphone audio on the speaker's device and
 * uplinks ~3-second WebM chunks to the backend's accessibility WebSocket.
 *
 * Admin-only.  Flow:
 *   1. Admin opens this page from the AdminEventPage → Accessibility tab.
 *   2. The page is pre-loaded with the active session_id + publish_token.
 *   3. Click "Start" → request mic permission → MediaRecorder slices the
 *      stream every 3 s → each chunk is sent as one binary WS frame.
 *   4. Server transcribes + translates + broadcasts to delegates.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Mic, MicOff, Loader2, ArrowLeft, Radio } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const CHUNK_MS = 3000;

const AccessibilitySource = () => {
  const { sessionId } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const eventId = params.get('event') || '';

  const [status, setStatus] = useState('idle'); // idle | requesting | live | stopping | error
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState(null);       // ready handshake from server
  const [chunkCount, setChunkCount] = useState(0);
  const [bytesSent, setBytesSent] = useState(0);
  const [serverAcks, setServerAcks] = useState(0);

  const wsRef = useRef(null);
  const mediaRef = useRef(null);
  const recorderRef = useRef(null);

  const start = async () => {
    if (!token) { setError('Missing publish token'); setStatus('error'); return; }
    setStatus('requesting');
    setError(null);
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 }
      });
    } catch (e) {
      setError(`Microphone access denied: ${e.message}`);
      setStatus('error');
      return;
    }
    mediaRef.current = stream;

    const wsUrl = `${API.replace(/^http/, 'ws')}/api/accessibility/ws/source/${sessionId}?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'ready') setMeta(msg);
        if (msg.type === 'ack') setServerAcks(c => c + 1);
        if (msg.type === 'error') { setError(msg.message); setStatus('error'); }
      } catch (_e) { /* binary or malformed — ignore */ }
    };
    ws.onclose = () => {
      if (status === 'live' || status === 'stopping') setStatus('idle');
      try { recorderRef.current?.stop(); } catch (_e) { /* already stopped */ }
      try { mediaRef.current?.getTracks().forEach(t => t.stop()); } catch (_e) { /* already stopped */ }
    };

    ws.onopen = () => {
      // Use a codec we know Whisper accepts. Each chunk needs to be a complete
      // container the server can decode independently, so we recycle the recorder
      // every CHUNK_MS rather than relying on dataavailable with timeslice
      // (which yields raw cluster fragments).
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus' : 'audio/webm';

      const startNewRecorder = () => {
        if (!mediaRef.current) return;
        const rec = new MediaRecorder(mediaRef.current, { mimeType: mime, audioBitsPerSecond: 32000 });
        recorderRef.current = rec;
        const parts = [];
        rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) parts.push(ev.data); };
        rec.onstop = async () => {
          if (parts.length === 0) return;
          const blob = new Blob(parts, { type: mime });
          const buf = await blob.arrayBuffer();
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            wsRef.current.send(buf);
            setChunkCount(c => c + 1);
            setBytesSent(b => b + buf.byteLength);
          }
          if (status !== 'stopping' && wsRef.current?.readyState === WebSocket.OPEN) {
            startNewRecorder();
          }
        };
        rec.start();
        setTimeout(() => { try { rec.stop(); } catch (_e) { /* already stopped */ } }, CHUNK_MS);
      };

      setStatus('live');
      startNewRecorder();
    };
  };

  const stop = () => {
    setStatus('stopping');
    try { wsRef.current?.send(JSON.stringify({ type: 'stop' })); } catch (_e) { /* socket gone */ }
    try { recorderRef.current?.stop(); } catch (_e) { /* already stopped */ }
    try { mediaRef.current?.getTracks().forEach(t => t.stop()); } catch (_e) { /* already stopped */ }
    try { wsRef.current?.close(); } catch (_e) { /* already closed */ }
    setStatus('idle');
  };

  useEffect(() => () => stop(), []); // best-effort cleanup on unmount

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 sm:p-8" data-testid="accessibility-source">
      <div className="max-w-2xl mx-auto space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-lg font-bold">Live caption source</h1>
        </div>

        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-400">Session</p>
                <p className="font-mono text-xs break-all">{sessionId}</p>
              </div>
              <Badge className={
                status === 'live' ? 'bg-emerald-600' :
                status === 'error' ? 'bg-red-600' :
                'bg-slate-700'
              } data-testid="accessibility-source-status">{status}</Badge>
            </div>

            {meta && (
              <div className="text-xs text-slate-400">
                Source: <b>{meta.source_lang}</b> · Targets: <b>{(meta.target_langs || []).join(', ')}</b>
              </div>
            )}
            {error && <p className="text-red-400 text-sm" data-testid="accessibility-source-error">{error}</p>}

            <div className="flex items-center justify-center py-6">
              {status === 'live' ? (
                <Button size="lg" variant="destructive" onClick={stop}
                        className="rounded-full h-24 w-24 flex flex-col gap-1"
                        data-testid="accessibility-source-stop">
                  <MicOff className="h-8 w-8" />
                  <span className="text-xs">Stop</span>
                </Button>
              ) : (
                <Button size="lg" onClick={start}
                        className="rounded-full h-24 w-24 flex flex-col gap-1 bg-emerald-600 hover:bg-emerald-700"
                        disabled={status === 'requesting'}
                        data-testid="accessibility-source-start">
                  {status === 'requesting' ? <Loader2 className="h-8 w-8 animate-spin" /> : <Mic className="h-8 w-8" />}
                  <span className="text-xs">Start</span>
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center text-xs">
              <div className="p-2 rounded bg-slate-800/60">
                <p className="text-slate-400">Chunks</p>
                <p className="font-bold text-base" data-testid="accessibility-source-chunks">{chunkCount}</p>
              </div>
              <div className="p-2 rounded bg-slate-800/60">
                <p className="text-slate-400">Sent</p>
                <p className="font-bold text-base">{(bytesSent / 1024).toFixed(1)} KB</p>
              </div>
              <div className="p-2 rounded bg-slate-800/60">
                <p className="text-slate-400">ACKs</p>
                <p className="font-bold text-base">{serverAcks}</p>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 leading-relaxed">
              <Radio className="inline h-3 w-3 mr-1" />
              Keep this tab open and unmuted for the whole talk. The browser will
              upload {CHUNK_MS / 1000}-second audio chunks. If you switch apps the
              recorder may pause — best is a dedicated phone/laptop near the desk.
              {eventId && <> · Event ID <span className="font-mono">{eventId}</span></>}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AccessibilitySource;
